<template>
  <div :class="['message-bubble-component', messageType]">
    <div class="message-content">
      <!-- 消息气泡 -->
      <div :class="['message-bubble', ...bubbleClass]">
        <!-- 消息文本 -->
        <div class="message-text">
          {{ displayContent }}
        </div>
        
        <!-- 消息元信息和操作按钮 -->
        <div class="message-footer">
          <div class="message-meta">
            <span class="message-time">{{ formattedTime }}</span>
            <span v-if="showReadStatus && messageType === 'own'" class="read-status">
              {{ readStatusText }}
            </span>
          </div>
          
          <!-- 消息操作 -->
          <div v-if="showActions" class="message-actions">
            <a-dropdown :trigger="['click']" :placement="messageType === 'own' ? 'topLeft' : 'topRight'">
              <a-button type="text" size="small" class="action-button">
                <template #icon><MoreOutlined /></template>
              </a-button>
              <template #overlay>
                <a-menu>
                  <!-- 翻译操作 -->
                  <a-menu-item
                    v-if="canTranslate"
                    @click="handleTranslate"
                    :disabled="translating"
                  >
                    <template #icon>
                      <TranslationOutlined />
                    </template>
                    {{ message.translated_content ? '重新翻译' : '翻译消息' }}
                  </a-menu-item>
                  
                  <!-- 复制消息 -->
                  <a-menu-item @click="handleCopy">
                    <template #icon>
                      <CopyOutlined />
                    </template>
                    复制消息
                  </a-menu-item>
                  
                  <!-- 标记已读 -->
                  <a-menu-item
                    v-if="canMarkRead"
                    @click="handleMarkRead"
                  >
                    <template #icon>
                      <CheckOutlined />
                    </template>
                    标记已读
                  </a-menu-item>
                </a-menu>
              </template>
            </a-dropdown>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  MoreOutlined,
  TranslationOutlined,
  CopyOutlined,
  CheckOutlined
} from '@ant-design/icons-vue'
import { message as antMessage } from 'ant-design-vue'
import { getRelativeTime } from '@/shared/utils'
import type { MessageResponse } from '../api'

// Props
interface Props {
  message: MessageResponse
  currentUserId: number
  showActions?: boolean
  showReadStatus?: boolean
  translating?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showActions: true,
  showReadStatus: true,
  translating: false
})

// Emits
const emit = defineEmits<{
  'translate': [messageId: number]
  'mark-read': [messageId: number]
}>()

// 计算属性
const messageType = computed(() => {
  return props.message.sender_id === props.currentUserId ? 'own' : 'other'
})

const displayContent = computed(() => {
  // 发送者看原文，接收者看译文
  if (messageType.value === 'own') {
    // 自己发送的消息，显示原文
    return props.message.original_content
  } else {
    // 别人发送的消息，优先显示译文，没有译文则显示原文
    return props.message.translated_content || props.message.original_content
  }
})

const messageLength = computed(() => {
  const content = displayContent.value
  if (content.length <= 10) return 'short'
  if (content.length <= 50) return 'medium'
  return 'long'
})

const bubbleClass = computed(() => {
  const classes = []
  if (messageLength.value === 'short') classes.push('short-message')
  if (messageLength.value === 'long') classes.push('long-message')
  if (props.translating) classes.push('translating')
  if (!props.message.is_read && messageType.value === 'other') classes.push('unread')
  return classes
})

const showTranslationInfo = computed(() => {
  // 不显示翻译状态信息
  return false
})

const translationStatusText = computed(() => {
  // 不显示翻译状态文本
  return ''
})

const formattedTime = computed(() => {
  return getRelativeTime(props.message.created_at)
})

const readStatusText = computed(() => {
  if (messageType.value === 'own') {
    return props.message.is_read ? '已读' : '未读'
  }
  return ''
})

const canTranslate = computed(() => {
  return showTranslationInfo.value && messageType.value === 'other'
})

const canMarkRead = computed(() => {
  return messageType.value === 'other' && !props.message.is_read
})

// 事件处理
const handleTranslate = () => {
  emit('translate', props.message.id)
}

const handleCopy = async () => {
  try {
    await navigator.clipboard.writeText(displayContent.value)
    antMessage.success('消息已复制到剪贴板')
  } catch (error) {
    console.error('复制失败:', error)
    antMessage.error('复制失败')
  }
}

const handleMarkRead = () => {
  emit('mark-read', props.message.id)
}
</script>

<style lang="less" scoped>
.message-bubble-component {
  margin-bottom: 12px;
  width: 100%;
  
  &.own {
    display: flex;
    justify-content: flex-end;
    
    .message-content {
      max-width: 70%;
      min-width: 60px;
      display: flex;
      flex-direction: column;
    }
    
    .message-bubble {
      background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
      color: white;
      border-radius: 18px 18px 4px 18px;
      box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15);
      
      // 短消息优化
      &.short-message {
        min-width: 60px;
        text-align: center;
      }
      
      // 长消息优化
      &.long-message {
        max-width: 100%;
      }
    }
  }
  
  &.other {
    display: flex;
    justify-content: flex-start;
    
    .message-content {
      max-width: 70%;
      min-width: 60px;
      display: flex;
      flex-direction: column;
    }
    
    .message-bubble {
      background-color: #ffffff;
      color: #333;
      border: 1px solid #f0f0f0;
      border-radius: 18px 18px 18px 4px;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
      
      // 短消息优化
      &.short-message {
        min-width: 60px;
      }
      
      // 长消息优化
      &.long-message {
        max-width: 100%;
      }
    }
  }
}

.message-content {
  display: flex;
  align-items: flex-end;
  gap: 4px;
}

.message-bubble {
  padding: 10px 14px;
  word-wrap: break-word;
  word-break: break-word;
  position: relative;
  width: 100%;
  box-sizing: border-box;
}

.message-text {
  line-height: 1.5;
  margin-bottom: 4px;
  font-size: 14px;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  hyphens: auto;
}

.message-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 4px;
  gap: 8px;
}

.message-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.message-time {
  font-size: 11px;
  opacity: 0.6;
  white-space: nowrap;
}

.read-status {
  font-size: 11px;
  opacity: 0.6;
  white-space: nowrap;
}

.message-actions {
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  flex-shrink: 0;
  order: -1; /* 将操作按钮放到左侧 */
}

.message-bubble-component:hover .message-actions {
  opacity: 1;
}

.action-button {
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.06);
  }
  
  .anticon {
    font-size: 12px;
  }
}

// 发送方消息的操作按钮样式
.message-bubble-component.own .action-button {
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
  
  .anticon {
    color: rgba(255, 255, 255, 0.8);
  }
}

// 接收方消息的操作按钮样式
.message-bubble-component.other .action-button {
  &:hover {
    background-color: rgba(0, 0, 0, 0.06);
  }
  
  .anticon {
    color: rgba(0, 0, 0, 0.5);
  }
}

// 发送方消息的特殊样式
.message-bubble-component.own {
  .message-footer {
    justify-content: space-between;
  }
  
  .message-meta {
    justify-content: flex-end;
    order: 1;
  }
  
  .message-actions {
    order: -1;
  }
  
  .message-time,
  .read-status {
    color: rgba(255, 255, 255, 0.8);
  }
}

// 接收方消息的特殊样式
.message-bubble-component.other {
  .message-footer {
    justify-content: space-between;
  }
  
  .message-meta {
    justify-content: flex-start;
    order: -1; /* 时间放到左侧 */
  }
  
  .message-actions {
    order: 1; /* 操作按钮放到右侧 */
  }
  
  .message-time,
  .read-status {
    color: rgba(0, 0, 0, 0.5);
  }
}

// 未读消息样式
.message-bubble-component.other .message-bubble {
  &.unread {
    background-color: #fff7e6;
    border: 1px solid #ffd591;
  }
}

// 翻译中状态
.message-bubble {
  &.translating {
    opacity: 0.7;
    position: relative;
    
    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 16px;
      height: 16px;
      border: 2px solid #1890ff;
      border-top: 2px solid transparent;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
  }
}

// 长消息优化
.message-text {
  // 确保长单词能够正确换行
  word-break: break-word;
  overflow-wrap: anywhere;
  
  // 保持合理的行高
  line-height: 1.4;
  
  // 处理连续空格和换行
  white-space: pre-wrap;
  
  // 确保文本不会溢出
  max-width: 100%;
  
  // 处理长URL和长单词
  hyphens: auto;
  
  // 确保中文文本正确换行
  word-wrap: break-word;
}

// 短消息特殊样式
.message-bubble {
  &.short-message {
    .message-text {
      text-align: center;
      min-width: 40px;
    }
    
    .message-footer {
      justify-content: space-between;
    }
  }
  
  &.long-message {
    .message-text {
      text-align: left;
    }
  }
}

// 发送方短消息居中对齐
.message-bubble-component.own .message-bubble.short-message {
  .message-text {
    text-align: center;
  }
}

// 接收方短消息左对齐
.message-bubble-component.other .message-bubble.short-message {
  .message-text {
    text-align: left;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .message-bubble-component {
    &.own .message-content,
    &.other .message-content {
      max-width: 85%;
    }
  }
  
  .message-bubble {
    padding: 8px 12px;
    
    &.short-message {
      padding: 6px 10px;
    }
  }
  
  .message-text {
    font-size: 13px;
  }
}

@keyframes spin {
  0% { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
}
</style>

